// Members Directory Page for Iron Forums

import { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { profilesAPI, jarvisAPI, forumsAPI } from '../api/client';
import { 
  Search, 
  MapPin, 
  Briefcase, 
  Users,
  User as UserIcon,
  Sparkles,
  Send,
  Loader2,
  Building2,
  Plus,
  Award,
  MessageSquare,
} from 'lucide-react';
import type { UserProfileListItem, ChatMessage, ForumListItem } from '../types';
import AskIfMemberCards, { membersFromAskIfAction } from '../components/AskIfMemberCards';
import DirectorySearchFilters, {
  applyDirectoryTagFilters,
  countDirectoryFilters,
  EMPTY_DIRECTORY_FILTERS,
  toProfileSearchFilters,
  type DirectoryFilterState,
} from '../components/DirectorySearchFilters';
import { useAuth } from '../context/useAuth';
import { publicRoleBadgeClass, publicRoleLabel } from '../utils/roles';

type TabType = 'jarvis' | 'search';

// Keep the Ask IF conversation (and which tab is open) across navigation —
// viewing a profile and coming back should not wipe the chat.
const CHAT_STORAGE_KEY = 'askif-directory-chat';
const TAB_STORAGE_KEY = 'askif-directory-tab';

function restoreChat(): ChatMessage[] {
  try {
    const raw = sessionStorage.getItem(CHAT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ChatMessage[]) : [];
  } catch {
    return [];
  }
}

export default function Members() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>(() =>
    sessionStorage.getItem(TAB_STORAGE_KEY) === 'jarvis' ? 'jarvis' : 'search',
  );
  
  // Search Directory state
  const [members, setMembers] = useState<UserProfileListItem[]>([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<DirectoryFilterState>(EMPTY_DIRECTORY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [forums, setForums] = useState<ForumListItem[]>([]);
  const [industryOptions, setIndustryOptions] = useState<string[]>([]);
  
  // Jarvis state
  const [jarvisMessages, setJarvisMessages] = useState<ChatMessage[]>(restoreChat);
  const [jarvisInput, setJarvisInput] = useState('');
  const [jarvisLoading, setJarvisLoading] = useState(false);
  const jarvisChatEndRef = useRef<HTMLDivElement>(null);
  const jarvisInputRef = useRef<HTMLInputElement>(null);

  // Load members and forums for Search Directory tab
  useEffect(() => {
    fetchMembers();
    fetchForums();
  }, []);

  // Server-side search: debounce the text query + structured filters to the
  // backend so search spans every profile field (and honors visibility +
  // profile-completeness rules) rather than only the fields shipped to the
  // client. Multi-tag (expertise/growth) filters are refined client-side.
  useEffect(() => {
    if (activeTab !== 'search') return;
    setIsSearching(true);
    const handle = setTimeout(() => {
      runServerSearch();
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    searchQuery,
    filters.state,
    filters.forumId,
    filters.industry,
    filters.userType,
    filters.familyStatus,
    filters.hasChildren,
    filters.minYears,
    filters.maxYears,
  ]);
  
  // Load Jarvis greeting when switching to Jarvis tab
  useEffect(() => {
    if (activeTab === 'jarvis' && jarvisMessages.length === 0) {
      loadJarvisGreeting();
    }
  }, [activeTab]);

  // Persist tab + conversation so leaving the page doesn't lose the chat.
  useEffect(() => {
    try {
      sessionStorage.setItem(TAB_STORAGE_KEY, activeTab);
    } catch { /* storage unavailable */ }
  }, [activeTab]);

  useEffect(() => {
    try {
      // Only persist real conversations, not the standalone greeting.
      if (jarvisMessages.length > 1) {
        sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(jarvisMessages));
      }
    } catch { /* storage unavailable */ }
  }, [jarvisMessages]);

  const clearJarvisChat = () => {
    sessionStorage.removeItem(CHAT_STORAGE_KEY);
    setJarvisMessages([]);
    loadJarvisGreeting();
  };
  
  // Auto-scroll Jarvis chat
  useEffect(() => {
    jarvisChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [jarvisMessages]);
  
  const loadJarvisGreeting = async () => {
    try {
      const { greeting } = await jarvisAPI.getFindMemberGreeting();
      setJarvisMessages([{ role: 'assistant', content: greeting }]);
    } catch (error) {
      console.error('Error loading Jarvis greeting:', error);
      setJarvisMessages([{ 
        role: 'assistant', 
        content: "Hi! I can help you find members. Who are you looking for?" 
      }]);
    }
  };
  
  const handleJarvisSend = async () => {
    if (!jarvisInput.trim() || jarvisLoading) return;
    
    const userMessage = jarvisInput.trim();
    setJarvisInput('');
    setJarvisMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setJarvisLoading(true);
    
    try {
      const result = await jarvisAPI.findMember(userMessage, jarvisMessages);
      
      setJarvisMessages(prev => [...prev, {
        role: 'assistant',
        content: result.response,
        members: membersFromAskIfAction(result.action),
      }]);
      
      // Refocus input
      setTimeout(() => jarvisInputRef.current?.focus(), 100);
    } catch (error) {
      console.error('Error with Jarvis:', error);
      setJarvisMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I encountered an issue. Please try again or use the Search Directory tab." 
      }]);
    } finally {
      setJarvisLoading(false);
    }
  };
  
  const fetchMembers = async () => {
    try {
      const data = await profilesAPI.getAll();
      setMembers(data);
      setTotalMembers(data.length);
      // Capture the full industry list once so the filter dropdown stays stable
      // even as the visible result set narrows under server-side search.
      setIndustryOptions((prev) => {
        if (prev.length) return prev;
        return [...new Set(data.map((m) => m.industry).filter(Boolean))].sort();
      });
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Translate the UI filter state into the backend's ProfileSearchFilters and
  // run a server-side search. Tag arrays (expertise/growth) stay client-side
  // so every selected tag is honored (OR within a picker).
  const runServerSearch = async () => {
    try {
      const data = await profilesAPI.search(
        searchQuery.trim(),
        toProfileSearchFilters(filters),
      );
      setMembers(data);
    } catch (error) {
      console.error('Error searching members:', error);
    } finally {
      setIsSearching(false);
    }
  };
  
  const fetchForums = async () => {
    try {
      const data = await forumsAPI.getAll();
      setForums(data);
    } catch (error) {
      console.error('Error fetching forums:', error);
    }
  };
  
  const uniqueIndustries = industryOptions;

  // The text query + structured filters are applied server-side (see
  // runServerSearch). Only the multi-tag (expertise/growth) refinements, which
  // the search endpoint doesn't OR together, are applied client-side here.
  const filteredMembers = useMemo(
    () => applyDirectoryTagFilters(members, filters),
    [members, filters],
  );

  const activeFilterCount = countDirectoryFilters(filters);

  const clearFilters = () => {
    setFilters(EMPTY_DIRECTORY_FILTERS);
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-navy">
              Member Directory
            </h1>
            <p className="text-gray-600 mt-1">
              Connect with {totalMembers} members in our community
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 bg-off-white px-4 py-2 rounded-lg">
              <Users className="text-gold" size={20} />
              <span className="text-sm font-medium text-gray-700">
                {activeTab === 'search' && (searchQuery || activeFilterCount > 0)
                  ? `${filteredMembers.length} of ${totalMembers} members`
                  : `${totalMembers} members`
                }
              </span>
            </div>
          </div>
        </div>
        
        {/* Tab Selector */}
        <div className="flex gap-2 mt-4 border-t border-gray-100 pt-4">
          <button
            onClick={() => {
              setActiveTab('search');
              setFilters((f) => ({ ...f, userType: '' }));
            }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition ${
              activeTab === 'search' && filters.userType !== 'advisor'
                ? 'bg-navy text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Search size={18} />
            Search Directory
          </button>
          <button
            onClick={() => {
              setActiveTab('search');
              setFilters((f) => ({ ...f, userType: 'advisor' }));
            }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition ${
              activeTab === 'search' && filters.userType === 'advisor'
                ? 'bg-navy text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Award size={18} />
            Advisor Corps
          </button>
          <button
            onClick={() => setActiveTab('jarvis')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition ${
              activeTab === 'jarvis'
                ? 'bg-navy text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Sparkles size={18} />
            Ask IF
          </button>
        </div>
      </div>

      {/* Jarvis Tab Content */}
      {activeTab === 'jarvis' && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
            <span className="flex items-center gap-2 text-sm font-medium text-navy">
              <Sparkles size={15} className="text-gold" />
              Ask IF member search
            </span>
            {jarvisMessages.length > 1 && (
              <button
                type="button"
                onClick={clearJarvisChat}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-500 hover:text-navy border border-gray-200 rounded-lg transition"
              >
                <Plus size={13} />
                New chat
              </button>
            )}
          </div>
          {/* Jarvis Chat Interface — fills the viewport instead of a fixed strip */}
          <div className="h-[calc(100dvh-25rem)] min-h-[400px] overflow-y-auto p-4 space-y-4">
            {jarvisMessages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'max-w-[80%] bg-navy text-white rounded-br-md'
                      : msg.members && msg.members.length > 0
                        ? 'w-full max-w-2xl bg-off-white text-gray-800 rounded-bl-md'
                        : 'max-w-[80%] bg-off-white text-gray-800 rounded-bl-md'
                  }`}
                >
                  {msg.role === 'assistant' && (
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles size={14} className="text-gold" />
                      <span className="text-xs font-medium text-gold">Ask IF</span>
                    </div>
                  )}
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                  {msg.role === 'assistant' && msg.members && msg.members.length > 0 && (
                    <AskIfMemberCards members={msg.members} />
                  )}
                </div>
              </div>
            ))}
            {jarvisLoading && (
              <div className="flex justify-start">
                <div className="bg-off-white rounded-2xl px-4 py-3 rounded-bl-md">
                  <div className="flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin text-gold" />
                    <span className="text-sm text-gray-500">Searching...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={jarvisChatEndRef} />
          </div>
          
          {/* Jarvis Input */}
          <div className="border-t border-gray-100 p-4">
            <div className="flex gap-2">
              <input
                ref={jarvisInputRef}
                type="text"
                value={jarvisInput}
                onChange={(e) => setJarvisInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJarvisSend()}
                placeholder="Describe who you're looking for..."
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20"
                disabled={jarvisLoading}
              />
              <button
                onClick={handleJarvisSend}
                disabled={!jarvisInput.trim() || jarvisLoading}
                aria-label="Send message"
                className="px-4 py-2.5 bg-navy text-white rounded-lg hover:bg-navy-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search Directory Tab Content */}
      {activeTab === 'search' && (
        <>
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <DirectorySearchFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              isSearching={isSearching}
              filters={filters}
              onFiltersChange={setFilters}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              showAdvanced={showAdvanced}
              onToggleAdvanced={() => setShowAdvanced(!showAdvanced)}
              forums={forums}
              industries={uniqueIndustries}
              onAskIf={() => setActiveTab('jarvis')}
            />
          </div>

          {/* Members Grid */}
          {filteredMembers.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 shadow-sm text-center">
              <Users className="mx-auto text-gray-300" size={48} />
              <h3 className="mt-4 text-lg font-medium text-gray-700">No members found</h3>
              <p className="text-gray-500 mt-1">
                {searchQuery || activeFilterCount > 0 
                  ? 'Try adjusting your search or filters'
                  : 'Members will appear here once they join'
                }
              </p>
              {(searchQuery || activeFilterCount > 0) && (
                <button
                  onClick={() => { setSearchQuery(''); clearFilters(); }}
                  className="mt-4 text-navy hover:underline"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredMembers.map((member) => (
                <Link
                  key={member.id}
                  to={`/members/${member.id}`}
                  className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition group"
                >
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="w-14 h-14 bg-off-white rounded-full flex items-center justify-center flex-shrink-0">
                      {member.avatar_url ? (
                        <img 
                          src={member.avatar_url} 
                          alt={member.full_name}
                          className="w-14 h-14 rounded-full object-cover"
                        />
                      ) : (
                        <UserIcon className="text-navy" size={28} />
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-navy group-hover:text-gold transition">
                        {member.full_name}
                      </h3>
                      
                      {/* Location & Industry */}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-gray-500">
                        {member.industry && (
                          <span className="flex items-center gap-1">
                            <Briefcase size={14} />
                            {member.industry}
                          </span>
                        )}
                        {(member.location_city || member.location_state || member.location_country) && (
                          <span className="flex items-center gap-1">
                            <MapPin size={14} />
                            {[member.location_city, member.location_state, member.location_country].filter(Boolean).join(', ')}
                          </span>
                        )}
                        {member.forum_location && (
                          <span className="flex items-center gap-1">
                            <Building2 size={14} />
                            {member.forum_location.name}
                          </span>
                        )}
                      </div>

                      {/* Bio */}
                      {member.bio && (
                        <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                          {member.bio}
                        </p>
                      )}

                      {/* Expertise Tags */}
                      {member.areas_of_expertise && member.areas_of_expertise.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {member.areas_of_expertise.slice(0, 3).map((exp, idx) => (
                            <span 
                              key={idx}
                              className="px-2 py-0.5 bg-off-white text-navy text-xs rounded-full"
                            >
                              {exp}
                            </span>
                          ))}
                          {member.areas_of_expertise.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                              +{member.areas_of_expertise.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Why this member matched the search */}
                  {member.match_reasons && member.match_reasons.length > 0 && (
                    <div className="mt-3 rounded-lg bg-gold/10 px-3 py-2 space-y-1">
                      {member.match_reasons.map((reason, idx) => (
                        <p key={idx} className="flex items-start gap-1.5 text-xs font-medium text-navy">
                          <Sparkles size={12} className="mt-0.5 shrink-0 text-gold" />
                          <span className="line-clamp-2">{reason}</span>
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Role Badge */}
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${publicRoleBadgeClass(member.role, member.snapshot_type)}`}>
                      {publicRoleLabel(member.role, member.snapshot_type)}
                    </span>
                    {member.id !== user?.id && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/messages?to=${member.id}`);
                        }}
                        className="flex items-center gap-1 px-2.5 py-1 pointer-coarse:py-2.5 text-xs font-medium text-navy border border-gray-200 rounded-lg hover:border-navy hover:bg-navy/[0.03] transition"
                        title={`Message ${member.full_name}`}
                      >
                        <MessageSquare size={12} />
                        Message
                      </button>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      )}

    </div>
  );
}
